import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Search,
  Dashboard,
  Assessment,
  Settings,
  Work,
  PersonAdd,
  Business,
  BarChart,
  TaskAlt,
  PrecisionManufacturing,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { searchPolicyAction } from "../redux/actions/dashboardAction";
import { clearGpId } from "../redux/actions/generalPartyInfoActions";

const MobileMenu = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subMenus, setSubMenus] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user);
  const dynamicTheme = useSelector((state) => state?.theme?.theme[0]);

  const roleAssigned = user?.newRole || [];
  const hasAuditorRole = roleAssigned.includes("ROLE_AUDITOR");
  const isEndUserOnly = roleAssigned.includes("ROLE_END_USER");
  const hasSuperRole = roleAssigned.includes("ROLE_SUPER");
  const hasCorporateRole = roleAssigned.includes("ROLE_CORPORATE");

  const showDashboardMenu = !hasAuditorRole && !isEndUserOnly;
  const showLeadsMenu = !hasAuditorRole && !isEndUserOnly;
  const showBotMenu = !hasAuditorRole && !isEndUserOnly;
  const showTaskManagement = !hasAuditorRole;
  const showReportMenu = !hasAuditorRole && !isEndUserOnly;
  const showAdminMenu = roleAssigned.length > 0 && !hasAuditorRole && !isEndUserOnly;
  const showCarrierGroup = hasSuperRole || hasCorporateRole;
  const showManageCarrier = hasSuperRole || hasCorporateRole;

  const handleNavigation = (path, state = {}) => () => {
    setDrawerOpen(false);
    dispatch(clearGpId);
    if (path === "/managepolicylisting") dispatch(searchPolicyAction());
    navigate(path, { state });
  };

  const handleToggleSubmenu = (key) => {
    setSubMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {/* Hamburger Icon */}
      <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "#000" }}>
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        disableScrollLock
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: "#ffffff",       
            color: "#000000",          
            borderRadius: "0 16px 16px 0",
          },
        }}
        >
        <Box sx={{ width: 280, height: "100%", bgcolor: "white", py: 2 }}>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              mb: 1,
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              color: dynamicTheme?.secondaryColor,
            }}
          >
            Menu
          </Typography>
          <Divider />

          <List>
            {/* DASHBOARD */}
            {showDashboardMenu && (
              <ListItemButton onClick={handleNavigation("/home")}>
                <ListItemIcon>
                  <Dashboard />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            )}

            {/* LEADS */}
            {showLeadsMenu && (
              <>
                <ListItemButton onClick={() => handleToggleSubmenu("leads")}>
                  <ListItemIcon>
                    <PersonAdd />
                  </ListItemIcon>
                  <ListItemText primary="Leads" />
                  {subMenus["leads"] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={subMenus["leads"]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/createlead")}>
                      <ListItemText primary="Create Lead" />
                    </ListItemButton>
                    <ListItemButton
                      sx={{ pl: 6 }}
                      onClick={handleNavigation("/allleads", { openCreateCustomer: true })}
                    >
                      <ListItemText primary="All Leads" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}

            {/* CRM */}
            <ListItemButton onClick={() => handleToggleSubmenu("crm")}>
              <ListItemIcon>
                <Business />
              </ListItemIcon>
              <ListItemText primary="CRM" />
              {subMenus["crm"] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={subMenus["crm"]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/globalsearch")}>
                  <ListItemIcon>
                    <Search />
                  </ListItemIcon>
                  <ListItemText primary="Search Customer" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 6 }}
                  onClick={handleNavigation("/globalsearch", { openCreateCustomer: true })}
                >
                  <ListItemText primary="Create Customer" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/createpolicy")}>
                  <ListItemText primary="Create Policy" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/policylist")}>
                  <ListItemText primary="Search Policy" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/")}>
                  <ListItemText primary="Create Quote" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/searchsavedquote")}>
                  <ListItemText primary="Search Quote" />
                </ListItemButton>
              </List>
            </Collapse>

            {/* BOT */}
            {showBotMenu && (
              <ListItemButton onClick={handleNavigation("/signupbot")}>
                <ListItemIcon>
                  <PrecisionManufacturing />
                </ListItemIcon>
                <ListItemText primary="Bot" />
              </ListItemButton>
            )}

            {/* TASK */}
            {showTaskManagement && (
              <ListItemButton onClick={handleNavigation("/taskmanagment")}>
                <ListItemIcon>
                  <TaskAlt />
                </ListItemIcon>
                <ListItemText primary="Task Management" />
              </ListItemButton>
            )}

            {/* REPORTS */}
            {showReportMenu && (
              <>
                <ListItemButton onClick={() => handleToggleSubmenu("reports")}>
                  <ListItemIcon>
                    <BarChart />
                  </ListItemIcon>
                  <ListItemText primary="Reports" />
                  {subMenus["reports"] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={subMenus["reports"]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/botreport")}>
                      <ListItemText primary="Bot Activity Log" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/botallreport")}>
                      <ListItemText primary="History Log" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/leadsourcereport")}>
                      <ListItemText primary="Production Report" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/customercrmlead")}>
                      <ListItemText primary="Customer CRM Lead Report" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/taskreport")}>
                      <ListItemText primary="Task Report" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/lastloginreport")}>
                      <ListItemText primary="Employee Log Report" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/raterreport")}>
                      <ListItemText primary="Rater Reports" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}

            {/* ADMIN */}
            {showAdminMenu && (
              <>
                <ListItemButton onClick={() => handleToggleSubmenu("admin")}>
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText primary="Admin" />
                  {subMenus["admin"] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={subMenus["admin"]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/locationmanager")}>
                      <ListItemText primary="Location List" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/locationgrp")}>
                      <ListItemText primary="Location Group" />
                    </ListItemButton>
                    {showManageCarrier && (
                      <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/managecarrier")}>
                        <ListItemText primary="Manage Carrier" />
                      </ListItemButton>
                    )}
                    {showCarrierGroup && (
                      <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/carriergroup")}>
                        <ListItemText primary="Carrier Group" />
                      </ListItemButton>
                    )}
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/userlist")}>
                      <ListItemText primary="User List" />
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleNavigation("/template")}>
                      <ListItemText primary="Templates" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileMenu;
